import type { UseFormPayload } from '@domain-group/react-hooks'

import usePublication from '@hooks/usePublication'
import usePublicationBooking, { usePublicationBookingForm } from '@hooks/usePublicationBooking'
import usePublicationBookings from '@hooks/usePublicationBookings'
import usePublicationOptions from '@hooks/usePublicationOptions'

import type { PublicationBookingModel, PublicationOptionModel } from '@models/types'

type SelectedPageOption = {
  id: number,
  value: string,
  node_name: string,
}

type UpdatePublicationBookingPageOptionParams = {
  publicationOption: PublicationOptionModel,
  selectedPageOptions: SelectedPageOption[],
  setEntityState: UseFormPayload['setEntityState'],
  value: string,
}

const updatePublicationBookingPageOption = (params: UpdatePublicationBookingPageOptionParams) => {
  const {
    publicationOption,
    selectedPageOptions,
    setEntityState,
    value,
  } = params

  const updatedPageOptions = [...selectedPageOptions]

  const pageOption: SelectedPageOption = {
    // @ts-expect-error TS2322
    id: publicationOption.id,
    value,
    // @ts-expect-error TS2322
    node_name: publicationOption.node_name,
  }

  const optionIndex = updatedPageOptions.findIndex(bookingOption => bookingOption.id === publicationOption.id)
  if (optionIndex !== -1){
    updatedPageOptions[optionIndex] = pageOption
  } else {
    updatedPageOptions.push(pageOption)
  }

  setEntityState({
    page_options: updatedPageOptions,
  })
}

function useManagePlannerBooking(initPublicationBooking: PublicationBookingModel) {
  // Publication Booking
  const publicationBookingPayload = usePublicationBooking(initPublicationBooking)
  const {
    callbacks: {
      deletePublicationBooking,
    },
    publicationBooking,
    publicationBooking: {
      publication_id,
    },
    publication,
    publicationEdition,
  } = publicationBookingPayload

  const publicationBookingsPayload = usePublicationBookings({
    filters: {
      order_by: 'publication_bookings.sort asc',
      publication_id: publication.id,
      // @ts-expect-error TS2339
      publication_edition_id: publicationEdition.id,
    },
  })
  const { filteredPublicationBookings } = publicationBookingsPayload

  // Publication Booking Form
  const publicationBookingFormPayload = usePublicationBookingForm(publicationBooking, { validateOn: 'change' })
  const {
    entityState: {
      page_options,
    },
    setEntityState,
  } = publicationBookingFormPayload

  // Publication
  const publicationPayload = usePublication(publication)
  const {
    notesEnabled,
    pagePositionEnabled,
  } = publicationPayload

  // Publication Options
  const publicationOptionsPayload = usePublicationOptions({
    filters: {
      publication_id,
    },
  })

  const selectedPageOptions = page_options || []

  return {
    callbacks: {
      deletePublicationBooking: () => deletePublicationBooking({ filteredPublicationBookings }),
      updatePublicationBookingPageOption: (value: string, publicationOption: PublicationOptionModel) => updatePublicationBookingPageOption({
        publicationOption,
        selectedPageOptions,
        setEntityState,
        value,
      }),
    },
    notesEnabled,
    pagePositionEnabled,
    publicationBookingFormPayload,
    publicationBookingPayload,
    publicationOptionsPayload,
    selectedPageOptions,
  }
}

export default useManagePlannerBooking
